import { atom } from 'jotai';
import localforage from 'localforage';
import configJs from '../../../config';
import { getLanguage } from '../../Services/UserService';
import { localForageEffect } from '../../store/jotai/effects/localForageEffect';
import { IMainMenuItem } from './types';

// --- Getters for hydration from IndexedDB (used in JotaiEffects) ---

export const getIsMainMenuOpenedFromIndexedDb = async (): Promise<boolean> => {
  const value = await localforage.getItem<boolean>('isMainMenuOpened');
  return value === null ? true : value;
};

export const getMenuUrlFromIndexedDb = async (): Promise<string> => {
  const url = await localforage.getItem<string>('menuUrl');
  return url === null ? configJs.Login.homeUrl : url;
};

export const getActiveMenuIdFromIndexedDb = async (): Promise<number> => {
  const id = await localforage.getItem<number>('activeMenuId');
  return id === null ? 1 : id;
};

export const getActiveMenuParentIdFromIndexedDb = async (): Promise<number> => {
  const id = await localforage.getItem<number>('activeMenuParentId');
  return id === null ? 0 : id;
};

export type MainMenuVariant = 'vertical' | 'horizontal';

// Temporary — re-enable when horizontal menu is ready for general use.
export const HORIZONTAL_MAIN_MENU_SWITCH_ENABLED = false;

export function getEffectiveMainMenuVariant(variant: MainMenuVariant): MainMenuVariant {
  return HORIZONTAL_MAIN_MENU_SWITCH_ENABLED ? variant : 'vertical';
}

export const getMainMenuVariantFromIndexedDb = async (): Promise<MainMenuVariant> => {
  const value = await localforage.getItem<MainMenuVariant>('mainMenuVariant');
  return value === null ? 'vertical' : value;
};

// --- Atoms (initial values; hydrated from API in JotaiEffects) ---

export const menuItemsAtom = atom<IMainMenuItem[]>([]);

export const activeMenuIdAtom = atom<number>(1);

export const activeMenuParentIdAtom = atom<number>(0);

export const isMenuShownAtom = atom<boolean>(true);

export const isSubMenuShownAtom = atom<boolean>(true);

export const activeLanguageAtom = atom<string | null>(getLanguage());

export const carRentalAtom = atom<string>('');

export const urlAtom = atom<string>(configJs.Login.homeUrl);

// Legacy page layouts still consume this flag to disable left offset.
export const isMainMenuDisabledAtom = atom<boolean>(false);

export const mainMenuVariantAtom = atom<MainMenuVariant>('vertical');

export type MainMenuTransformState = 'collapsed' | 'expanded' | 'hover';

export function menuOpenToTransformState(open: boolean): MainMenuTransformState {
  return open ? 'expanded' : 'collapsed';
}

export function transformStateToMenuOpen(state: MainMenuTransformState): boolean {
  return state === 'expanded';
}

export const isMainMenuCollapsedAtom = atom<MainMenuTransformState>('collapsed');

export const activeAccountMenuKeyAtom = atom<string | null>(null);
export const isParentMenuItemAtom = atom<boolean>(false);

/** Synthetic parent ids for sticky bottom menus (not real menu items). */
export const ACCOUNT_PARENT_ID = -999;
export const CONTACT_PARENT_ID = -998;

// --- Effects for persistence ---

export const activeMenuIdEffectAtom = localForageEffect(activeMenuIdAtom, 'activeMenuId');

export const activeMenuParentIdEffectAtom = localForageEffect(
  activeMenuParentIdAtom,
  'activeMenuParentId'
);

export const isMenuShownEffectAtom = localForageEffect(isMenuShownAtom, 'isMainMenuOpened');

export const urlEffectAtom = localForageEffect(urlAtom, 'menuUrl');

export const mainMenuVariantEffectAtom = localForageEffect(mainMenuVariantAtom, 'mainMenuVariant');
